/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Select, Table, Tag, Typography, message } from "antd";
import OptionService from "../../../../service/ERP/Options.service";

const optionService = OptionService();

/**
 * จับคู่ชื่อสินค้าอิสระของ Proforma Invoice กับรหัสสินค้าในระบบ
 * ต้องจับคู่ให้ครบทุกแถวถึงจะกดยืนยันได้ — Invoice บังคับต้องมีรหัสสินค้า
 *
 * rows    : รายการสินค้าปัจจุบันของ Invoice
 * confirm : ([{ index, pg_code, pg_name, unit_cs }]) => void
 */
export default function ModalMapProducts({ open, close, rows = [], confirm }) {
  const [products, setProducts] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    optionService
      .optionsPG({ p: "cus" })
      .then((res) => setProducts(res.data?.data || []))
      .catch(() => message.error("โหลดรายการสินค้าไม่สำเร็จ"))
      .finally(() => setLoading(false));

    /* ตั้งค่าเริ่มต้นจากแถวที่มีรหัสอยู่แล้ว */
    const init = {};
    rows.forEach((r, i) => {
      if (r.pg_code) init[i] = r.pg_code;
    });
    setMapping(init);
  }, [open]);

  const options = useMemo(
    () =>
      products.map((p) => ({
        value: p.pg_code,
        label: `${p.pg_code} — ${p.pg_name}`,
        raw: p,
      })),
    [products]
  );

  /* เดาให้ก่อน : ชื่อสินค้าใน PI ตรงกับชื่อในระบบแบบไม่สนตัวพิมพ์ */
  const autoMatch = () => {
    const next = { ...mapping };
    let hit = 0;
    rows.forEach((r, i) => {
      if (next[i]) return;
      const name = String(r.pi_pg_name || r.pg_name || "").trim().toLowerCase();
      if (!name) return;
      const found = products.find(
        (p) =>
          String(p.pg_name || "").trim().toLowerCase() === name ||
          String(p.pg_ivname || "").trim().toLowerCase() === name
      );
      if (found) {
        next[i] = found.pg_code;
        hit += 1;
      }
    });
    setMapping(next);
    message.info(hit > 0 ? `จับคู่อัตโนมัติได้ ${hit} รายการ` : "ไม่พบชื่อที่ตรงกันอัตโนมัติ");
  };

  const remaining = rows.filter((_, i) => !mapping[i]).length;

  const handleOk = () => {
    if (remaining > 0) {
      message.warning(`ยังเหลืออีก ${remaining} รายการที่ยังไม่ได้จับคู่`);
      return;
    }
    const result = rows.map((_, i) => {
      const p = products.find((x) => x.pg_code === mapping[i]);
      return {
        index: i,
        pg_code: mapping[i],
        pg_name: p?.pg_ivname || p?.pg_name || "",
        unit_cs: p?.unit_cs,
        /* น้ำหนัก/ขนาดบรรจุ ดึงจาก Packing Goods ของสินค้าที่จับคู่ */
        net_wt: p?.net_wt,
        gross_wt: p?.gross_wt,
        packing_size: p?.packing_size,
      };
    });
    confirm?.(result);
    close?.(false);
    message.success("จับคู่รหัสสินค้าเรียบร้อย");
  };

  const columns = [
    { title: "ลำดับ", key: "seq", width: 60, align: "center", render: (_, __, i) => i + 1 },
    {
      title: "ชื่อสินค้าใน Proforma Invoice",
      key: "pi_name",
      render: (_, r) => (
        <Typography.Text>{r.pi_pg_name || r.pg_name || "-"}</Typography.Text>
      ),
    },
    {
      title: "จับคู่กับสินค้าในระบบ",
      key: "map",
      width: 380,
      render: (_, r, i) => (
        <Select
          showSearch
          allowClear
          style={{ width: "100%" }}
          placeholder="เลือกรหัสสินค้า"
          optionFilterProp="label"
          options={options}
          value={mapping[i]}
          onChange={(v) => setMapping((prev) => ({ ...prev, [i]: v }))}
          status={mapping[i] ? "" : "warning"}
        />
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      width: 110,
      align: "center",
      render: (_, __, i) =>
        mapping[i] ? <Tag color="green">จับคู่แล้ว</Tag> : <Tag color="orange">ยังไม่จับคู่</Tag>,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={() => close?.(false)}
      onOk={handleOk}
      okText="ยืนยันการจับคู่"
      cancelText="ปิด"
      width={1000}
      title="จับคู่รหัสสินค้าก่อนออก Invoice"
      okButtonProps={{ disabled: remaining > 0 }}
      destroyOnClose
    >
      <Alert
        type={remaining > 0 ? "warning" : "success"}
        showIcon
        className="!mb-3"
        message={
          remaining > 0
            ? `Invoice ต้องมีรหัสสินค้าครบทุกรายการ — ยังเหลืออีก ${remaining} รายการ`
            : "จับคู่ครบทุกรายการแล้ว"
        }
        action={
          <Button size="small" onClick={autoMatch} loading={loading}>
            จับคู่อัตโนมัติจากชื่อ
          </Button>
        }
      />
      <Table
        size="small"
        rowKey={(_, i) => `map-${i}`}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ y: 380 }}
      />
    </Modal>
  );
}
